import type { CreateEventOptions, EventRecord } from '../../types'
import type { EventKey } from '../../types/event-keys'
import { buildEvent } from './buildEvent.js'
import { createEvent } from './createEvent.js'

/**
 * Validate, build, and persist an event log entry in a single call.
 *
 * ```typescript
 * // Fire-and-forget — don't slow down the caller
 * logEvent('move.created', { user: 'admin@example.com', role: 'admin' })
 *
 * // Await when you need the result
 * const event = await logEvent('move.created', { user, role, move_id: 42 })
 * ```
 *
 * Metadata is automatically validated against the registry Zod schema (if one
 * exists) unless `skipMetadataValidation` is set to `true`.
 *
 * @param key - An {@link EventKey} string.
 * @param options - Event details including optional `executor` override.
 * @returns The persisted {@link EventRecord}.
 *
 * @throws {@link EventValidationError} if required fields are missing, have
 *   invalid types, or metadata fails schema validation.
 * @throws {@link EventKeyNotFoundError} if the event key doesn't match a
 *   known row in the database.
 * @throws {@link RoleTypeNotFoundError} if the role name doesn't match a
 *   known role type in the database.
 */
export async function logEvent(
  key: EventKey,
  options: CreateEventOptions,
): Promise<EventRecord> {
  const { executor, ...rest } = options

  const eventlogInput = await buildEvent({ ...rest, key }, executor)
  const inserted = await createEvent(eventlogInput, executor)

  return {
    id: inserted.id,
    event_time_utc: inserted.event_time_utc,
    created_at_utc: inserted.created_at_utc,
    key,
    user: inserted.user,
    role: options.role,
    metadata: inserted.metadata,
    customer_id: inserted.customer_id ?? null,
    move_id: inserted.move_id ?? null,
    lane_id: inserted.lane_id ?? null,
    location_id: inserted.location_id ?? null,
    driver_id: inserted.driver_id ?? null,
  }
}

